/**
 * DonateButton.jsx — small CTA + dialog that lets a reader donate coins
 * to the scribe who created the content they're consuming.
 *
 * Props:
 *   recipientId  — required: target scribe's user_id
 *   recipientName — optional display name shown in dialog
 *   className    — outer button classes (defaults to a pill)
 *
 * Hidden when the viewer is the recipient (no self-donation), or not
 * authenticated, or the recipient_id is missing.
 */
import React, { useEffect, useState } from 'react';
import { Coins, Loader2, Heart } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const QUICK_AMOUNTS = [50, 100, 250, 500];
const MIN = 50;

const DonateButton = ({ recipientId, recipientName, className }) => {
  const [open, setOpen] = useState(false);
  const [coins, setCoins] = useState(100);
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState(null);

  // Resolve viewer + reader balance when the dialog opens.
  const refreshBalance = async () => {
    try {
      const meRes = await authFetch(`${BACKEND_URL}/api/auth/me`);
      const meData = await meRes.json();
      setMe(meData);
      const stRes = await authFetch(`${BACKEND_URL}/api/reader/stats`);
      const stData = await stRes.json();
      setBalance(stData?.coinBalance ?? 0);
    } catch {
      /* ignore — guarded UI below */
    }
  };

  useEffect(() => {
    if (open) refreshBalance();
  }, [open]);

  // Hide the button entirely when there's nothing meaningful to donate to.
  if (!recipientId) return null;
  if (me && (me.user_id === recipientId || me.id === recipientId)) return null;

  const submit = async () => {
    if (coins < MIN) {
      toast.error(`Minimum donation is ${MIN}🪙.`);
      return;
    }
    if (balance != null && coins > balance) {
      toast.error(`You only have ${balance}🪙.`);
      return;
    }
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/coins/donate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_id: recipientId, coins: parseInt(coins, 10) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(
        `Sent ${data.coins_sent}🪙 → ${data.coins_received}🪙 to ${data.recipient_username || 'the scribe'}`,
      );
      setOpen(false);
    } catch (err) {
      toast.error(err.message || 'Donation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        className={
          className ||
          'border-pink-500/40 text-pink-300 hover:bg-pink-500/10 gap-1.5'
        }
        data-testid="donate-button"
      >
        <Heart className="w-4 h-4" />
        <span>Tip the scribe</span>
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white"
          data-testid="donate-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-300" />
              Tip {recipientName || 'the scribe'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Send coins straight to the scribe. They receive 1.5× what you send (100🪙 = £1.50).
              Minimum {MIN}🪙.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="text-xs text-slate-300">
              Your balance:{' '}
              <span className="font-bold text-white">
                {balance == null ? '…' : `${balance}🪙`}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map((amt) => (
                <Button
                  key={amt}
                  type="button"
                  variant={coins === amt ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCoins(amt)}
                  className={
                    coins === amt
                      ? 'bg-amber-500 hover:bg-amber-400 text-slate-900'
                      : 'border-slate-600 text-slate-200'
                  }
                  data-testid={`donate-quick-${amt}`}
                >
                  {amt}🪙
                </Button>
              ))}
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Custom amount</label>
              <Input
                type="number"
                min={MIN}
                step={1}
                value={coins}
                onChange={(e) => setCoins(parseInt(e.target.value, 10) || 0)}
                className="bg-slate-800 border-slate-700"
                data-testid="donate-coin-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Scribe will receive <span className="text-amber-300 font-semibold">{Math.round((coins || 0) * 1.5)}🪙</span>
                {' '}({(((coins || 0) * 1.5) / 100).toFixed(2).replace(/\.00$/, '')} GBP equivalent on cash-out).
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="border-slate-600 text-slate-200"
              data-testid="donate-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={busy || coins < MIN || (balance != null && coins > balance)}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold disabled:opacity-50"
              data-testid="donate-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
              Donate {coins}🪙
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DonateButton;
